import { goToReferral, trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { getAppVersion } from '../../utils/sessionStorage'
import { isDevEnvironment } from '../../utils/utils'
import type { EliteOnboardingStatusResponse } from '../../types/misc.types'
import { ELiteMembershipStatus } from '../../types/mutual-fund-detail.types'
import {
  getEliteCurrentPlanTenure,
  isEliteYearlyPlanTenure
} from './dashboard/eliteOnboardingFocusPlan'
import { useEliteBuyCtaExtras } from './EliteBuyCtaExtrasContext'

export const MIN_APP_VERSION_ELITE_REFERRAL_INVITE = 93

export function isEliteReferralInviteEnabled(): boolean {
  if (isDevEnvironment()) {
    return true
  }

  const rawVersion = getAppVersion()
  if (rawVersion === '' || rawVersion == null) {
    return false
  }
  const appVersion = Number(rawVersion)
  if (!Number.isFinite(appVersion)) {
    return false
  }
  return appVersion >= MIN_APP_VERSION_ELITE_REFERRAL_INVITE
}

export function isEliteMemberForReferralInvite(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null
): boolean {
  if (eliteOnboardingStatus?.subscriptionDetails?.status === ELiteMembershipStatus.ELITE) {
    return true
  }
  return eliteOnboardingStatus?.userSubscriptionDetails?.memberShipStatus === ELiteMembershipStatus.ELITE
}

export function isYearlyElitePlanForReferralInvite(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null
): boolean {
  return isEliteYearlyPlanTenure(getEliteCurrentPlanTenure(eliteOnboardingStatus ?? undefined))
}

/** App version ≥ 93, active Elite yearly member, and not family landing/dashboard. */
export function shouldShowEliteReferralInviteBanner(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null,
  options?: { isFamilyMode?: boolean }
): boolean {
  if (options?.isFamilyMode || eliteOnboardingStatus?.throughFamilyAccess) return false
  if (!isEliteReferralInviteEnabled()) return false
  if (!isEliteMemberForReferralInvite(eliteOnboardingStatus)) return false
  if (!isYearlyElitePlanForReferralInvite(eliteOnboardingStatus)) return false
  return true
}

export function EliteReferralInviteBanner({
  className = '',
  screenName,
  onClick
}: {
  className?: string
  screenName: string
  onClick?: () => void
}) {
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  function handleClick() {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...eliteBuyCtaExtras,
        screenType: screenName,
        eventId: 'Referral_Invite_Banner_Clicked'
      }
    })
    onClick ? onClick() : goToReferral()
  }

  return (
    <div
      onClick={handleClick}
      className={`relative flex min-h-[54px] cursor-pointer items-center overflow-hidden rounded-xl bg-[radial-gradient(552.78%_89.1%_at_10.9%_50%,_#FEEFDE_50%,_#F9E3CA_100%)] py-[14px] pr-4 pl-[72px] ${className}`}
    >
      <img
        src='/images/new-elite/referral/invite-elite-cards.png'
        alt=''
        className='pointer-events-none absolute bottom-[-8px] left-[-4px] h-[80px] w-auto object-contain'
      />
      <div className='flex min-w-0 flex-1 items-center'>
        <div className='min-w-0 flex-1 text-left'>
          <p className='text-[12px] leading-[1.3] text-[#BB4F22]'>Invite a friend, extend your Elite</p>
          <p className='text-[13px] font-medium leading-[1.3] text-[#232323]'>
            Get 3 months free with each referral
          </p>
        </div>
        <EliteReferralInviteChevronIcon />
      </div>
    </div>
  )
}

export function EliteReferralInviteChevronIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={12}
      height={12}
      viewBox='0 0 12 12'
      fill='none'
      className='size-3 shrink-0'
      aria-hidden
    >
      <path
        d='M9.00045 4.92146L4.08045 0.241457C3.99425 0.159512 3.89276 0.0953479 3.78176 0.0526279C3.67076 0.00990789 3.55244 -0.0105313 3.43354 -0.00752266C3.31464 -0.00451404 3.1975 0.0218835 3.08881 0.0701626C2.98012 0.118442 2.882 0.187657 2.80005 0.273857C2.63456 0.447945 2.545 0.680646 2.55107 0.920767C2.55715 1.16089 2.65836 1.38876 2.83245 1.55426L7.28685 5.78426C7.31641 5.81218 7.33996 5.84585 7.35606 5.8832C7.37215 5.92055 7.38045 5.96079 7.38045 6.00146C7.38045 6.04212 7.37215 6.08236 7.35606 6.11971C7.33996 6.15706 7.31641 6.19073 7.28685 6.21866L2.83245 10.4487C2.65932 10.613 2.55858 10.8395 2.55239 11.0781C2.5462 11.3168 2.63507 11.5481 2.79945 11.7213C2.96383 11.8944 3.19026 11.9951 3.42892 12.0013C3.66758 12.0075 3.89892 11.9186 4.07205 11.7543L9.01245 7.06226C9.15302 6.92134 9.26429 6.75398 9.33983 6.56983C9.41537 6.38568 9.45369 6.18839 9.45258 5.98936C9.45146 5.79032 9.41093 5.59347 9.33333 5.41018C9.25573 5.22689 9.14259 5.06079 9.00045 4.92146Z'
        fill='#8E9393'
      />
    </svg>
  )
}
